import { useState } from 'react';
import { Field, Select, TextInput } from '../../components/Field';
import { TypeAhead, type TypeAheadItem } from '../../components/TypeAhead';
import { ErrorBanner } from '../../components/ErrorBanner';
import { parseAmountInput } from '../../lib/money';
import type { LedgerWritePayload, UpdateLedgerPayload } from '../../lib/api';
import type { AccountGroup, Ledger, LedgerType, PartyRegistrationType } from '../../lib/types';

interface LedgerFormProps {
  groups: AccountGroup[];
  initial?: Ledger | null;
  onSave: (payload: LedgerWritePayload | UpdateLedgerPayload) => Promise<void>;
  onCancel: () => void;
  busy: boolean;
  error: unknown;
}

const LEDGER_TYPES: { value: LedgerType; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'party', label: 'Party (debtor/creditor)' },
  { value: 'bank', label: 'Bank' },
  { value: 'cash', label: 'Cash' },
  { value: 'tax', label: 'Tax' },
  { value: 'stock', label: 'Stock' },
  { value: 'round_off', label: 'Round off' },
];

const PARTY_REGISTRATION_TYPES: Array<{ value: PartyRegistrationType; label: string }> = [
  { value: 'regular', label: 'Regular registered' },
  { value: 'composition', label: 'Composition taxpayer' },
  { value: 'unregistered', label: 'Unregistered' },
  { value: 'sez', label: 'SEZ unit' },
  { value: 'sez_developer', label: 'SEZ developer' },
  { value: 'overseas', label: 'Overseas' },
  { value: 'deemed_export', label: 'Deemed export' },
  { value: 'uin', label: 'UIN holder' },
  { value: 'isd', label: 'Input Service Distributor (ISD)' },
];

const REGISTRATIONS_REQUIRING_GSTIN = new Set<PartyRegistrationType>([
  'regular', 'composition', 'sez', 'sez_developer', 'uin', 'isd',
]);

export function LedgerForm({ groups, initial, onSave, onCancel, busy, error }: LedgerFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [alias, setAlias] = useState(initial?.alias ?? '');
  const [groupId, setGroupId] = useState(initial?.groupId ?? '');
  const [groupLabel, setGroupLabel] = useState(() => groups.find((g) => g.id === initial?.groupId)?.name ?? '');
  const [ledgerType, setLedgerType] = useState<LedgerType>(initial?.ledgerType ?? 'general');
  const [openingBalance, setOpeningBalance] = useState(initial?.openingBalance ?? '0.0000');
  const [openingBalanceType, setOpeningBalanceType] = useState<'dr' | 'cr'>(initial?.openingBalanceType ?? 'dr');
  const [gstin, setGstin] = useState(initial?.gstin ?? '');
  // Existing legacy GSTINs intentionally start unselected. We never imply “regular” merely
  // because a GSTIN is present; the accountant must confirm the legal registration status.
  const [registrationType, setRegistrationType] = useState<PartyRegistrationType | ''>(
    initial?.registrationType ?? (initial?.gstin ? '' : 'unregistered'),
  );
  const [stateCode, setStateCode] = useState(initial?.stateCode ?? '');
  const [creditLimit, setCreditLimit] = useState(initial?.creditLimit ?? '');
  const [creditDays, setCreditDays] = useState(initial?.creditDays?.toString() ?? '');
  const [bankName, setBankName] = useState(initial?.bankName ?? '');
  const [accountNumber, setAccountNumber] = useState(initial?.accountNumber ?? '');
  const [ifsc, setIfsc] = useState(initial?.ifsc ?? '');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [partyError, setPartyError] = useState<string | null>(null);

  const groupItems: TypeAheadItem[] = groups.map((g) => ({ key: g.id, label: g.name }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!groupId) return;
    const parsed = parseAmountInput(openingBalance);
    if (parsed === null) {
      setAmountError('Enter a valid amount, e.g. 12,345.00');
      return;
    }
    setAmountError(null);
    if (ledgerType === 'party') {
      if (!registrationType) {
        setPartyError('Choose the party GST registration status. A GSTIN does not determine it automatically.');
        return;
      }
      if (gstin && registrationType === 'unregistered') {
        setPartyError('An Unregistered party cannot have a GSTIN. Choose the applicable registered status.');
        return;
      }
      if (gstin && !/^\d{2}$/.test(stateCode)) {
        setPartyError('Enter the two-digit GST state code with a GSTIN.');
        return;
      }
      if (gstin && stateCode && !gstin.startsWith(stateCode)) {
        setPartyError('The first two GSTIN digits must match the GST state code.');
        return;
      }
      if (REGISTRATIONS_REQUIRING_GSTIN.has(registrationType) && !gstin) {
        setPartyError(`${PARTY_REGISTRATION_TYPES.find((x) => x.value === registrationType)?.label} needs a GSTIN.`);
        return;
      }
    }
    setPartyError(null);
    const payload: LedgerWritePayload = {
      name, alias: alias || null, groupId, ledgerType, openingBalance: parsed, openingBalanceType,
      ...(ledgerType === 'party' ? {
        gstin: gstin || null,
        registrationType: registrationType as PartyRegistrationType,
        stateCode: stateCode || null,
        creditLimit: creditLimit ? parseAmountInput(creditLimit) : null,
        creditDays: creditDays ? Number(creditDays) : null,
      } : {}),
      ...(ledgerType === 'bank' ? { bankName: bankName || null, accountNumber: accountNumber || null, ifsc: ifsc || null } : {}),
    };
    await onSave(initial ? { ...payload, revision: initial.revision } : payload);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {error ? <ErrorBanner error={error} /> : null}
      <Field label="Name" htmlFor="lf-name" required>
        <TextInput id="lf-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <Field label="Alias" htmlFor="lf-alias" hint="Optional short name for type-ahead search.">
        <TextInput id="lf-alias" value={alias} onChange={(e) => setAlias(e.target.value)} />
      </Field>
      <Field label="Group" htmlFor="lf-group" required>
        <TypeAhead
          items={groupItems}
          onSelect={(item) => {
            setGroupId(item.key);
            setGroupLabel(item.label);
          }}
          placeholder={groupLabel || 'Search groups…'}
        />
        {groupId && <p className="mt-0.5 text-[11px] text-[var(--text-faint)]">Selected: {groupLabel}</p>}
      </Field>
      <Field label="Ledger type" htmlFor="lf-type" required>
        <Select id="lf-type" value={ledgerType} onChange={(e) => setLedgerType(e.target.value as LedgerType)} disabled={Boolean(initial?.isSystem)}>
          {LEDGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </Field>

      <div className="flex gap-3">
        <Field label="Opening balance" htmlFor="lf-ob" className="flex-1" error={amountError}>
          <TextInput id="lf-ob" inputMode="decimal" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} className="num" />
        </Field>
        <Field label="Type" htmlFor="lf-obt" className="w-28">
          <Select id="lf-obt" value={openingBalanceType} onChange={(e) => setOpeningBalanceType(e.target.value as 'dr' | 'cr')}>
            <option value="dr">Debit</option>
            <option value="cr">Credit</option>
          </Select>
        </Field>
      </div>

      {ledgerType === 'party' && (
        <div className="flex flex-col gap-3 border-t border-[var(--border)] pt-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Party details</p>
          {partyError ? <p className="rounded-[3px] border border-[var(--danger)] bg-[var(--danger-bg)] px-2 py-1.5 text-[11px] text-[var(--danger)]">{partyError}</p> : null}
          <Field label="GST registration status" htmlFor="lf-registration" required hint="Choose this explicitly; a GSTIN never implies Regular registered.">
            <Select id="lf-registration" value={registrationType} onChange={(e) => setRegistrationType(e.target.value as PartyRegistrationType | '')}>
              <option value="">Choose status</option>
              {PARTY_REGISTRATION_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </Select>
          </Field>
          <div className="flex gap-3">
          <Field label="GSTIN" htmlFor="lf-gstin" className="flex-1" hint="Required for registered, composition, SEZ, UIN and ISD parties.">
            <TextInput id="lf-gstin" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} maxLength={15} />
          </Field>
          <Field label="GST state code" htmlFor="lf-state" className="w-36" hint="e.g. 27">
            <TextInput id="lf-state" value={stateCode} onChange={(e) => setStateCode(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" maxLength={2} />
          </Field>
          </div>
          <div className="flex gap-3">
            <Field label="Credit limit" htmlFor="lf-credit-limit" className="flex-1">
              <TextInput id="lf-credit-limit" inputMode="decimal" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} className="num" />
            </Field>
            <Field label="Credit days" htmlFor="lf-credit-days" className="w-28">
              <TextInput id="lf-credit-days" type="number" min={0} value={creditDays} onChange={(e) => setCreditDays(e.target.value)} className="num" />
            </Field>
          </div>
        </div>
      )}

      {ledgerType === 'bank' && (
        <div className="flex flex-col gap-3 border-t border-[var(--border)] pt-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Bank details</p>
          <Field label="Bank name" htmlFor="lf-bank">
            <TextInput id="lf-bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
          </Field>
          <Field label="Account number" htmlFor="lf-acct">
            <TextInput id="lf-acct" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
          </Field>
          <Field label="IFSC" htmlFor="lf-ifsc">
            <TextInput id="lf-ifsc" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} maxLength={11} />
          </Field>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="rounded-[3px] border border-[var(--border)] px-3 py-1.5 text-[12.5px]">
          Cancel
        </button>
        <button type="submit" disabled={busy || !groupId} className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50">
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create ledger'}
        </button>
      </div>
    </form>
  );
}
